const CITATION_START = '\uE200'
const CITATION_STOP = '\uE201'
const CITATION_DELIMITER = '\uE202'
const CITATION_PREFIX = `${CITATION_START}cite${CITATION_DELIMITER}`
// Match the backend recovery bound so an orphaned envelope cannot swallow the rest of a response.
const CITATION_MARKER_MAX_RUNES = 2048

export type CitationMarkerFilterStats = {
  completeEnvelopeCount: number
  incompleteEnvelopeCount: number
}

export type CitationMarkerFilterResult = {
  text: string
  stats: CitationMarkerFilterStats
}

/**
 * Removes citation-family envelopes without retaining their payloads. Until the complete
 * citation prefix is recognized, at most the fixed-length prefix candidate is buffered.
 *
 * OpenAI defines the well-formed envelope grammar, but not malformed-stream recovery.
 * Recovery here intentionally matches the backend: confirmed envelopes fail closed,
 * nested starts begin a fresh candidate, and oversized envelopes are abandoned.
 */
export class CitationMarkerFilter {
  private prefixCandidate = ''
  private suppressingEnvelope = false
  private suppressedRuneCount = 0
  private completeEnvelopeCount = 0
  private incompleteEnvelopeCount = 0

  push(text: string): string {
    const output: string[] = []

    for (const character of text) {
      if (this.suppressingEnvelope) {
        if (character === CITATION_STOP) {
          this.suppressingEnvelope = false
          this.suppressedRuneCount = 0
          this.completeEnvelopeCount++
        } else if (character === CITATION_START) {
          // Nested envelopes are not valid grammar. Discard the confirmed malformed
          // envelope and parse this start fresh so later ordinary text can recover.
          this.suppressingEnvelope = false
          this.suppressedRuneCount = 0
          this.incompleteEnvelopeCount++
          this.prefixCandidate = character
        } else {
          this.suppressedRuneCount++
          if (this.suppressedRuneCount > CITATION_MARKER_MAX_RUNES) {
            // Drop the bound-crossing rune because it is still indistinguishable from
            // marker payload, then resume ordinary output with the following rune.
            this.suppressingEnvelope = false
            this.suppressedRuneCount = 0
            this.incompleteEnvelopeCount++
          }
        }
        continue
      }

      if (!this.prefixCandidate) {
        if (character === CITATION_START) {
          this.prefixCandidate = character
        } else {
          output.push(character)
        }
        continue
      }

      this.prefixCandidate += character
      if (CITATION_PREFIX.startsWith(this.prefixCandidate)) {
        if (this.prefixCandidate === CITATION_PREFIX) {
          this.prefixCandidate = ''
          this.suppressingEnvelope = true
          this.suppressedRuneCount = CITATION_PREFIX.length
        }
        continue
      }

      const nestedStart = this.prefixCandidate.lastIndexOf(CITATION_START)
      if (nestedStart > 0) {
        output.push(this.prefixCandidate.slice(0, nestedStart))
        this.prefixCandidate = this.prefixCandidate.slice(nestedStart)
      } else {
        output.push(this.prefixCandidate)
        this.prefixCandidate = ''
      }
    }

    return output.join('')
  }

  finish(): CitationMarkerFilterResult {
    // Release an unconfirmed candidate unchanged, but keep a confirmed unclosed
    // envelope suppressed because its payload cannot safely be identified as prose.
    const text = this.prefixCandidate
    const stats = {
      completeEnvelopeCount: this.completeEnvelopeCount,
      incompleteEnvelopeCount: this.incompleteEnvelopeCount + (this.suppressingEnvelope ? 1 : 0),
    }

    this.prefixCandidate = ''
    this.suppressingEnvelope = false
    this.suppressedRuneCount = 0
    this.completeEnvelopeCount = 0
    this.incompleteEnvelopeCount = 0

    return {text, stats}
  }
}

export function filterCitationMarkers(text: string): CitationMarkerFilterResult {
  const filter = new CitationMarkerFilter()
  const streamedText = filter.push(text)
  const result = filter.finish()
  return {...result, text: streamedText + result.text}
}
